import { PayloadAction } from '@reduxjs/toolkit';

import { CcdaDocumentClient, CcdaDocumentListItemDto, CcdaGenerateRequest, CcdaGenerateResultDto } from '@medone/medonehp-api-client';

import { handleError } from '../../../../shared/common/HandleErrors';
import { shouldSkipByPatientId } from '../../../../shared/common/helpers';
import { Axios } from '../../../../shared/common/http';
import { AppDispatch, AppThunk, RootState } from '../../../../shared/store';
import { CensusState } from './models';
import { censusSlice } from './slice';

export const reducers = {
    setCcdaDocuments: (state: CensusState, action: PayloadAction<CcdaDocumentListItemDto[]>) => {
        state.ccdaDocuments = action.payload;
    },
};

// Per-patient generated-documents list (newest first server-side).
export function fetchCcdaDocuments(patientId: number): AppThunk {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        const client = new CcdaDocumentClient(null, Axios);

        try {
            const response = await client.getByPatient(patientId);

            // Guard against race condition: discard if user navigated to a different patient
            if (shouldSkipByPatientId(getState().census, patientId)) {
                return;
            }

            if (!response.result.succeeded) {
                handleError(response, () => true);
            } else {
                dispatch(censusSlice.actions.setCcdaDocuments(response.result.entity ?? []));
            }
        } catch (error) {
            handleError(error, () => true);
        }
    };
}

// Runs bundle-build → Zen → persist synchronously (seconds — callers show a spinner).
// ⚠ SCRUM-926 contract: the envelope's `succeeded` stays TRUE on a Zen document
// rejection — the failure signal is entity.errors being non-empty / ccdaDocumentId
// being null. This thunk therefore returns the entity for BOTH outcomes and callers
// branch on it; only transport-level failures (succeeded=false, throw) return null.
export function generateCcdaDocument(request: CcdaGenerateRequest): AppThunk<Promise<CcdaGenerateResultDto | null>> {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        const client = new CcdaDocumentClient(null, Axios);

        try {
            const response = await client.generate(request);

            if (!response.result.succeeded || response.result.entity == null) {
                handleError(response, () => true);

                return null;
            }

            const result = response.result.entity;

            // A persisted document changes the per-patient list — refresh it so the
            // modal's parent list is current without a manual reload.
            if (result.ccdaDocumentId != null && request.patientId != null) {
                await dispatch(fetchCcdaDocuments(request.patientId));
            }

            return result;
        } catch (error) {
            // 400s carry user-presentable ProblemDetails detail; handleError's
            // ProblemDetails branch surfaces it. 500s fall back to generic copy.
            handleError(error, () => true);

            // Generate is NOT atomic: a failure after the document row commits still
            // returns 500 while the row exists (later visible with uscdiCoverage null).
            // Refresh so a partially-created document is not silently hidden.
            if (request.patientId != null) {
                await dispatch(fetchCcdaDocuments(request.patientId));
            }

            return null;
        }
    };
}

// Raw C-CDA XML for the preview. Deliberately NOT stored in redux: views are
// audited server-side and the handoff forbids caching the body across sessions.
export async function fetchCcdaDocumentContent(ccdaDocumentId: number): Promise<string | null> {
    const client = new CcdaDocumentClient(null, Axios);

    try {
        const response = await client.getContent(ccdaDocumentId);

        return await response.result.data.text();
    } catch (error) {
        // 404 = document (or blob body) gone; surface and let the caller show empty.
        handleError(error, () => true);

        return null;
    }
}

export const selectCcdaDocuments = (state: RootState) => state.census.ccdaDocuments;
